import styles from './styles.module.css'

import React from 'react'

import { Box, Button, DiscoCallout, Flex, Text, TextField } from 'components/ds'

import { RightsLoading } from '../rights-loading'

export type NoResultsFormInputs = {
  isrc: string
  title: string
  artist: string
}

type Props = {
  inputs: NoResultsFormInputs
  isLoading: boolean
  calloutText: string
  onChange: (next: NoResultsFormInputs) => void
  onTryAgain: () => void
  onClose: () => void
}

const isValid = ({ isrc, title, artist }: NoResultsFormInputs) =>
  isrc.trim().length > 0 ||
  (title.trim().length > 0 && artist.trim().length > 0)

const NoResultsForm = ({
  inputs,
  isLoading,
  calloutText,
  onChange,
  onTryAgain,
  onClose,
}: Props) => {
  const handleField =
    (field: keyof NoResultsFormInputs) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...inputs, [field]: e.target.value })
    }

  const canSubmit = !isLoading && isValid(inputs)

  return (
    <Flex direction="column" gap="3">
      <DiscoCallout type="neutral">{calloutText}</DiscoCallout>

      <Box>
        <Text size="2" weight="medium" as="label" htmlFor="rights-isrc-input">
          ISRC
        </Text>
        <Box mt="2">
          <TextField.Root
            id="rights-isrc-input"
            value={inputs.isrc}
            onChange={handleField('isrc')}
            disabled={isLoading}
            placeholder="ISRC"
          />
        </Box>
      </Box>

      <Flex align="center" gap="2">
        <div className={styles.divider} />
        <Text size="1" color="gray">
          OR
        </Text>
        <div className={styles.divider} />
      </Flex>

      <Flex gap="2" align="end">
        <Box flexGrow="1" flexBasis="0">
          <Text
            size="2"
            weight="medium"
            as="label"
            htmlFor="rights-title-input"
          >
            Track title
          </Text>
          <Box mt="2">
            <TextField.Root
              id="rights-title-input"
              value={inputs.title}
              onChange={handleField('title')}
              disabled={isLoading}
              placeholder="Track title"
            />
          </Box>
        </Box>
        <Box flexGrow="1" flexBasis="0">
          <Text
            size="2"
            weight="medium"
            as="label"
            htmlFor="rights-artist-input"
          >
            Artist
          </Text>
          <Box mt="2">
            <TextField.Root
              id="rights-artist-input"
              value={inputs.artist}
              onChange={handleField('artist')}
              disabled={isLoading}
              placeholder="Artist"
            />
          </Box>
        </Box>
      </Flex>

      {isLoading && <RightsLoading />}

      <Flex justify="end" align="center" gap="4">
        <Button
          className={styles.closeButton}
          size="2"
          type="button"
          variant="ghost"
          onClick={onClose}
          tabIndex={0}
        >
          Close
        </Button>
        <Button
          className={styles.tryAgainButton}
          color="gray"
          highContrast
          size="2"
          onClick={onTryAgain}
          disabled={!canSubmit}
          tabIndex={0}
        >
          Try again
        </Button>
      </Flex>
    </Flex>
  )
}

export default NoResultsForm
